/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import usePaginatedDropdownOptions from '../../../hooks/usePaginatedDropdownOptions';
import Tooltip from '../../Tooltip';
import Checkbox from '../Checkbox';
import useOnClickOutside from '../../../hooks/useOnClickOutside';
import classes from './styles.module.scss';

export default function GroupedMultiDropdown({
  options,
  value,
  placeholder,
  width,
  dangerOption,
  height,
  label,
  error,
  touched,
  searchInfo,
  searchInfoWidth,
  name,
  setFieldValue,
  setFieldTouched,
  info,
  note,
  infoWidth,
  fetchOptions,
  maxLength,
  readOnly,
  labelStyle,
  style,
  icon,
  hideValue,
  bottomPosition,
  noPortal,
}) {
  const [isOptionsListVisible, setIsOptionsListVisible] = useState(false);
  const [isEnoughHeight, setIsEnoughHeight] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [optionsCoords, setOptionsCoords] = useState(null);

  const selectorRef = useRef();
  const dropdownRef = useRef();
  const optionsRef = useRef();
  const isOpened = useRef();

  const { t } = useTranslation();

  useOnClickOutside(
    optionsRef,
    () => setIsOptionsListVisible(false),
    selectorRef
  );

  const setMultiDropdownOption = (option) => {
    if (Array.isArray(value)) {
      if (value.some((val) => val.value === option.value)) {
        const newValues = value.filter((val) => val.value !== option.value);

        if (!newValues.length) {
          setFieldValue?.(name, '');
        } else {
          setFieldValue?.(name, newValues);
        }
      } else {
        if (maxLength && maxLength === value?.length) {
          return;
        }

        setFieldValue?.(name, [...value, option]);
      }
    } else {
      setFieldValue?.(name, [option]);
    }
  };

  useEffect(() => {
    if (bottomPosition) {
      return;
    }

    if (isOptionsListVisible && optionsRef.current && dropdownRef.current) {
      dropdownRef.current.style.setProperty(
        '--optionsHeight',
        `-${optionsRef.current.clientHeight}px`
      );

      const { bottom } = optionsRef.current.getBoundingClientRect();

      if (window.innerHeight - bottom < dropdownRef.current.clientHeight) {
        setIsEnoughHeight(false);
      } else {
        setIsEnoughHeight(true);
      }
    } else {
      setIsEnoughHeight(true);
      if (isOpened.current) {
        setFieldTouched?.(name, true, true);
      }
    }
  }, [isOptionsListVisible, name, setFieldTouched, bottomPosition]);

  useEffect(() => {
    if (!isOptionsListVisible) {
      setSearchTerm('');
      setOptionsCoords(null);
    }
  }, [isOptionsListVisible]);

  // Hide options list when user scrolls outside of it
  useEffect(() => {
    const hideOptionsOnScroll = (event) => {
      if (!optionsRef.current?.contains(event.target)) {
        setIsOptionsListVisible(false);
      }
    };

    const preventArrowKeysScroll = (event) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
      }
    };

    if (isOptionsListVisible) {
      window.addEventListener('wheel', hideOptionsOnScroll);
      window.addEventListener('keydown', preventArrowKeysScroll);
    } else {
      window.removeEventListener('wheel', hideOptionsOnScroll);
      window.removeEventListener('keydown', preventArrowKeysScroll);
    }
  }, [isOptionsListVisible]);

  useEffect(() => {
    if (isOptionsListVisible && selectorRef.current) {
      const {
        top,
        bottom,
        left,
        width: selectorWidth,
      } = selectorRef.current.getBoundingClientRect();

      setOptionsCoords({
        top: bottom + 10,
        left,
        width: selectorWidth,
      });

      setTimeout(() => {
        if (
          optionsRef.current &&
          dropdownRef.current &&
          dropdownRef.current.classList.contains(classes.optionsTop)
        ) {
          setOptionsCoords({
            top: 'auto',
            bottom: window.innerHeight - top + 5,
            left,
            width: selectorWidth,
            isUpward: true,
          });
        }
      }, 0);
    }
  }, [isOptionsListVisible]);

  const { options: fetchedOptions, lastListElementRef } =
    usePaginatedDropdownOptions({
      dataName: fetchOptions?.dataName,
      getDataHandler: fetchOptions?.getDataHandler,
      searchTerm,
      labelName: fetchOptions?.labelName,
      isUserList: fetchOptions?.isUserList,
      isCompanyList: fetchOptions?.isCompanyList,
      isExternalUserList: fetchOptions?.isExternalUserList,
      fetchHanlderOptions: fetchOptions?.fetchHanlderOptions,
    });

  const optionsToDisplay = fetchOptions
    ? fetchedOptions
    : options?.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      );

  // Group options by category
  const groupedOptions = useMemo(() => {
    if (!optionsToDisplay || optionsToDisplay.length === 0) {
      return {};
    }

    const groups = {};
    optionsToDisplay.forEach((option) => {
      const category = option.categoryName || 'Other';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(option);
    });

    return groups;
  }, [optionsToDisplay]);

  const categoryNames = Object.keys(groupedOptions);

  const searchAndOptions = (
    <div
      className={classes.searchAndOptions}
      ref={optionsRef}
      style={{
        top: optionsCoords?.top,
        left: optionsCoords?.left,
        position: optionsCoords ? 'fixed' : 'absolute',
        width: optionsCoords?.width,
        bottom: optionsCoords?.bottom,
        zIndex: 99999,
        ...(optionsCoords?.isUpward && {
          display: 'flex',
          flexDirection: 'column-reverse',
          gap: '5px',
        }),
      }}
    >
      <div className={classes.search}>
        <input
          type="text"
          placeholder={t('common.search')}
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
        {searchInfo && (
          <Tooltip text={searchInfo} width={searchInfoWidth}>
            <i>Info</i>
          </Tooltip>
        )}
      </div>
      <div className={classes.options}>
        {searchTerm && !categoryNames.length ? (
          <div className={classes.emptyState}>
            {t('common.noResultsFoundMessage')}
          </div>
        ) : (
          <ul>
            {categoryNames.map((categoryName, categoryIndex) => (
              <React.Fragment key={categoryName}>
                <li className={classes.categoryHeader}>{categoryName}</li>
                {groupedOptions[categoryName].map((option, optionIndex) => {
                  const isLastOption =
                    categoryIndex === categoryNames.length - 1 &&
                    optionIndex === groupedOptions[categoryName].length - 1;

                  return (
                    <li
                      ref={isLastOption ? lastListElementRef : null}
                      key={option.value}
                      onClick={() => {
                        setMultiDropdownOption(option);
                      }}
                      className={classNames(classes.optionItem, {
                        [classes.active]: value?.some?.(
                          (val) => val.value === option.value
                        ),
                        [classes.danger]: dangerOption === option.value,
                      })}
                    >
                      <Checkbox
                        isSelected={value?.some?.(
                          (val) => val.value === option.value
                        )}
                      />
                      {option.label}
                    </li>
                  );
                })}
              </React.Fragment>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  return (
    <div
      style={{
        width,
      }}
      tabIndex={0}
      role="listbox"
      ref={dropdownRef}
      className={classNames(classes.GroupedMultiDropdown, {
        [classes.open]: isOptionsListVisible,
        [classes.error]: error && touched,
        [classes.optionsTop]: !isEnoughHeight,
      })}
    >
      <span className={classes.label} style={labelStyle}>
        {label}{' '}
        {info && (
          <Tooltip text={info} width={infoWidth} direction="top-right">
            <i className={classes.infoIcon}>
              <svg
                width="3"
                height="10"
                viewBox="0 0 3 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 1.25H1.03125"
                  stroke="#242833"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M1 8.75V4.75"
                  stroke="#242833"
                  strokeWidth="2"
                  strokeLinecap="square"
                  strokeLinejoin="round"
                />
              </svg>
            </i>
          </Tooltip>
        )}{' '}
        {note && <span className={classes.note}>({note})</span>}
      </span>
      <div
        style={{ ...style, width, height }}
        className={classNames(classes.selector, {
          [classes.withIcon]: icon,
        })}
        ref={selectorRef}
        onClick={() => {
          if (readOnly) {
            return;
          }
          isOpened.current = true;
          setIsOptionsListVisible((prevState) => !prevState);
        }}
      >
        {value && Array.isArray(value) && value.length && !hideValue ? (
          <span className={classes.hasOption}>
            {value?.map((val) => val.label)?.join(', ')}
          </span>
        ) : (
          <span>{placeholder}</span>
        )}
        {!readOnly && <div className={classes.arrow} />}
        {icon && <img src={icon} alt="icon" className={classes.icon} />}
      </div>
      <div className={classes.error}>{error}</div>
      {isOptionsListVisible && (
        <>
          {optionsCoords && !noPortal ? (
            createPortal(searchAndOptions, document.body)
          ) : (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 9999,
                backgroundColor: 'white',
                border: '1px solid #ccc',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }}
            >
              {searchAndOptions}
            </div>
          )}
        </>
      )}
    </div>
  );
}
